/**
 * PointDetailPopup — data point detail overlay shown on click in SLD viewer.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import type {
  DataPointConfig,
  DataPointRuntimeState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
  AlarmState,
} from '@/types/monitoring';
import type { SLDPointMapping } from '@/stores/monitoringStore';
import {
  stateClass,
  stateTextClass,
  clamp,
  formatMetricValue,
  opLabel,
} from './sldUtils';

export interface PointDetailPopupProps {
  mapping: SLDPointMapping;
  position: { x: number; y: number };
  datapoints: DataPointConfig[];
  runtimeStates: Map<string, DataPointRuntimeState>;
  advancedRules?: AdvancedAlarmRule[];
  triggeredAdvancedRules?: TriggeredAdvancedRule[];
  displayMetric: string;
  onDisplayMetricChange: (metric: string) => void;
  onSave: () => void;
  onRemove: (dpId: string) => void;
  onLocateOnMap: (dpId: string) => void;
  onEdit?: (dpId: string) => void;
  onClose: () => void;
}

export default function PointDetailPopup({
  mapping,
  position,
  datapoints,
  runtimeStates,
  advancedRules: advancedRulesProp,
  triggeredAdvancedRules: triggeredAdvancedProp,
  displayMetric,
  onDisplayMetricChange,
  onSave,
  onRemove,
  onLocateOnMap,
  onEdit,
  onClose,
}: PointDetailPopupProps) {
  const config = datapoints.find((dp) => dp.id === mapping.dataPointId);
  const runtime = runtimeStates.get(mapping.dataPointId);
  const state: AlarmState = runtime?.state ?? 'no_data';
  const values = runtime?.values ?? {};
  const metrics = Object.entries(values);
  const availableMetrics = config?.selected_metrics ?? [];

  // Data-point-level triggered rules
  const triggeredRules = runtime?.triggered_rules ?? [];
  const triggeredGroups = runtime?.triggered_groups ?? [];

  // Advanced rules triggered and referencing this data point's entity
  const entityId = config?.entity_id;
  const allAdvancedRules = advancedRulesProp ?? [];
  const allTriggeredAdvanced = triggeredAdvancedProp ?? [];
  const relevantTriggeredAdvanced: Array<{
    rule: typeof allAdvancedRules[number];
    triggered: typeof allTriggeredAdvanced[number];
  }> = [];

  for (const triggered of allTriggeredAdvanced) {
    const rule = allAdvancedRules.find((r) => r.id === triggered.rule_id);
    if (!rule) continue;
    const referencesEntity = triggered.matched_metrics?.some(
      (m) => m.entity_id === entityId,
    );
    if (referencesEntity) {
      relevantTriggeredAdvanced.push({ rule, triggered });
    }
  }

  const hasTriggeredRules =
    triggeredRules.length > 0 ||
    triggeredGroups.length > 0 ||
    relevantTriggeredAdvanced.length > 0;

  const popupStyle: React.CSSProperties = {
    left: clamp(position.x + 10, 16, window.innerWidth - 340),
    top: clamp(position.y - 100, 16, window.innerHeight - 350),
  };

  return (
    <div className="sld-detail-popup" style={popupStyle}>
      <div className="sld-detail-header">
        <span className={`sld-detail-header__dot ${stateClass(state)}`} />
        <span className="sld-detail-header__name">{config?.display_name ?? 'Unknown'}</span>
        <span className={`sld-detail-header__state ${stateTextClass(state)}`}>
          {state.toUpperCase()}
        </span>
      </div>

      {config && (
        <div className="sld-detail-entity">
          Entity: {config.entity_id}
        </div>
      )}

      {metrics.length > 0 && (
        <div className="sld-detail-metrics">
          {metrics.map(([key, val]) => (
            <div key={key} className="sld-detail-metric-row">
              <span className="sld-detail-metric-key">{key}</span>
              <span className="sld-detail-metric-val">
                {formatMetricValue(val, config?.decimals)}
                {(() => { const u = config?.metric_units?.[key] ?? config?.unit; return u ? ` ${u}` : ''; })()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Triggered Rules */}
      {hasTriggeredRules && (
        <div className="sld-detail-rules">
          <div className="sld-detail-rules__title">Triggered Rules</div>

          {triggeredRules.map((tr) => (
            <div key={tr.rule_id} className={`sld-detail-rule sld-detail-rule--${tr.severity}`}>
              <span className="sld-detail-rule__badge">{tr.severity.toUpperCase()}</span>
              <span className="sld-detail-rule__detail">
                {tr.metric} {opLabel(tr.op)} {tr.value}
                {tr.actual != null && (
                  <span className="sld-detail-rule__actual">
                    {' '}(actual: {formatMetricValue(tr.actual, config?.decimals)})
                  </span>
                )}
              </span>
              {tr.message && <div className="sld-detail-rule__msg">{tr.message}</div>}
            </div>
          ))}

          {triggeredGroups.map((tg) => (
            <div key={tg.group_id} className={`sld-detail-rule sld-detail-rule--${tg.severity}`}>
              <span className="sld-detail-rule__badge">{tg.severity.toUpperCase()}</span>
              <span className="sld-detail-rule__detail">
                {tg.group_name || 'Rule Group'} ({tg.combinator})
              </span>
              {tg.message && <div className="sld-detail-rule__msg">{tg.message}</div>}
              <div className="sld-detail-rule__conditions">
                {tg.triggered_conditions.map((c, i) => (
                  <div key={i} className="sld-detail-rule__cond">
                    {c.metric} {opLabel(c.op)} {c.value}
                    {c.actual != null && (
                      <span className="sld-detail-rule__actual">
                        {' '}(actual: {formatMetricValue(c.actual, config?.decimals)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {relevantTriggeredAdvanced.map(({ rule, triggered }) => (
            <div key={rule.id} className={`sld-detail-rule sld-detail-rule--${triggered.severity}`}>
              <span className="sld-detail-rule__badge">{triggered.severity.toUpperCase()}</span>
              <span className="sld-detail-rule__detail">
                {triggered.rule_name}
                <span className="sld-detail-rule__type">{rule.type.replace('_', ' ')}</span>
              </span>
              {triggered.details && <div className="sld-detail-rule__msg">{triggered.details}</div>}
            </div>
          ))}
        </div>
      )}

      {availableMetrics.length > 0 && (
        <div className="sld-detail-display-metric">
          <label>Display Metric on Diagram</label>
          <select value={displayMetric} onChange={(e) => onDisplayMetricChange(e.target.value)}>
            <option value="">None (label only)</option>
            <option value="__all__">All selected metrics</option>
            {availableMetrics.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="sld-detail-actions">
        {availableMetrics.length > 0 && (
          <button className="primary" onClick={onSave}>
            Save
          </button>
        )}
        {onEdit && (
          <button onClick={() => { onEdit(mapping.dataPointId); onClose(); }}>
            Edit
          </button>
        )}
        {config?.coordinates && (
          <button onClick={() => onLocateOnMap(mapping.dataPointId)}>Map</button>
        )}
        <button
          className="danger"
          onClick={() => {
            onRemove(mapping.dataPointId);
            onClose();
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}
